/**
 * The landing craft's bow ramp.
 *
 * The Blender model hangs the ramp from a `ramp_hinge` node; this swings that
 * node about the boat's athwartships axis. Closed is 0 (standing up, sealing
 * the bow); open drops it forward and down until its lip rests on the ground,
 * or to the full open angle in deep water.
 */

import { Quaternion, Vector3, type Object3D } from 'three/webgpu'
import type { Vessel } from '../physics/Vessel.ts'

/** Hinge to lip, metres, matching the Blender ramp plate. */
export const RAMP_LENGTH = 1.86
/** About 126 degrees: in deep water the lip hangs nearly a metre below the hinge. */
const OPEN_ANGLE = 2.2
/** Radians per second; a full swing takes about two and a half seconds. */
const SWING_RATE = .8
/** Past this the ramp is low enough to drive on. */
const DRIVABLE_ANGLE = 1.2

const _tip = new Vector3()
const _axis = new Quaternion()

export class LandingRamp {
  open = false
  /** Current swing, radians from closed. */
  angle = 0
  /** Where the ramp's travel stops this frame (ground or full open). */
  private limit = 0
  /** Hinge position in the vessel frame (+Z bow, Y up). */
  readonly hinge = new Vector3()
  private readonly axisInParent = new Vector3(1, 0, 0)
  private readonly closedQuaternion: Quaternion

  constructor(private readonly node: Object3D, model: Object3D) {
    model.updateMatrixWorld(true)
    node.getWorldPosition(this.hinge)
    // The boat's +X axis, expressed in the hinge's parent frame.
    const parentRotation = new Quaternion()
    node.parent?.getWorldQuaternion(parentRotation)
    this.axisInParent.applyQuaternion(parentRotation.invert())
    this.closedQuaternion = node.quaternion.clone()
  }

  toggle(): boolean {
    this.open = !this.open
    return this.open
  }

  /** Snap shut, e.g. when a new mode starts. */
  reset(): void {
    this.open = false
    this.angle = 0
    this.limit = 0
    this.apply()
  }

  /** 0 closed .. 1 fully open. */
  get openFraction(): number {
    return this.angle / OPEN_ANGLE
  }

  /** Open, finished moving, and low enough for a forklift. */
  get down(): boolean {
    return this.open && this.angle >= DRIVABLE_ANGLE && Math.abs(this.angle - this.limit) < .01
  }

  /** The ramp lip in the vessel frame at a given swing angle. */
  tipLocal(target: Vector3, angle = this.angle): Vector3 {
    return target.set(this.hinge.x, this.hinge.y + RAMP_LENGTH * Math.cos(angle), this.hinge.z + RAMP_LENGTH * Math.sin(angle))
  }

  tipWorld(vessel: Vessel, target: Vector3, angle = this.angle): Vector3 {
    return vessel.localToWorld(this.tipLocal(_tip, angle), target)
  }

  /**
   * Swing toward the requested position. With a ground function the ramp
   * stops where its lip meets the sand, and lifts again if the boat settles.
   */
  update(dt: number, vessel: Vessel, groundAt: ((x: number, z: number) => number) | null): void {
    let limit = this.open ? OPEN_ANGLE : 0
    if (this.open && groundAt) {
      const touches = (angle: number): boolean => {
        this.tipWorld(vessel, _tip, angle)
        return _tip.y <= groundAt(_tip.x, _tip.z)
      }
      if (touches(OPEN_ANGLE)) {
        // The lip's height falls steadily across this range.
        let low = .6, high = OPEN_ANGLE
        if (touches(low)) high = low
        else for (let i = 0; i < 12; i++) {
          const mid = (low + high) / 2
          if (touches(mid)) high = mid
          else low = mid
        }
        limit = high
      }
    }
    this.limit = limit
    const step = SWING_RATE * dt
    if (this.angle < limit) this.angle = Math.min(limit, this.angle + step)
    else this.angle = Math.max(limit, this.angle - (this.open ? Infinity : step))
    this.apply()
  }

  private apply(): void {
    this.node.quaternion.copy(_axis.setFromAxisAngle(this.axisInParent, this.angle)).multiply(this.closedQuaternion)
  }
}
